export const locales = ["en", "ro"] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = "en";

export const SITE_URL = "https://www.egodent.co.uk";

export function isLocale(value: string | undefined | null): value is Locale {
  return !!value && (locales as readonly string[]).includes(value);
}

const EXTERNAL = /^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i;

/** Prefix an internal path with the locale (English stays unprefixed). */
export function localizePath(locale: Locale, href: string): string {
  if (!href || EXTERNAL.test(href) || !href.startsWith("/")) return href;
  if (locale === defaultLocale) return href;
  if (href === "/ro" || href.startsWith("/ro/") || href.startsWith("/ro?") || href.startsWith("/ro#")) {
    return href;
  }
  return href === "/" ? "/ro" : `/ro${href}`;
}

/** Split a browser pathname into its locale and the locale-free path. */
export function splitLocale(pathname: string): { locale: Locale; path: string } {
  if (pathname === "/ro" || pathname.startsWith("/ro/")) {
    return { locale: "ro", path: pathname.slice(3) || "/" };
  }
  // Prerendered English pages report their internal /en/... path; treat it as the public one.
  if (pathname === "/en" || pathname.startsWith("/en/")) {
    return { locale: "en", path: pathname.slice(3) || "/" };
  }
  return { locale: "en", path: pathname || "/" };
}

/** Absolute URL for a locale-free path. */
export function absoluteUrl(locale: Locale, path: string, host: string = SITE_URL): string {
  const p = localizePath(locale, path);
  return p === "/" ? `${host}/` : `${host}${p}`;
}

/** Canonical + hreflang alternates for a locale-free path. */
export function alternatesFor(locale: Locale, path: string, host: string = SITE_URL) {
  return {
    canonical: absoluteUrl(locale, path, host),
    languages: {
      "en-GB": absoluteUrl("en", path, host),
      ro: absoluteUrl("ro", path, host),
      "x-default": absoluteUrl("en", path, host),
    },
  };
}
